import React, { useMemo } from 'react';

/**
 * ScoreWaterfall — SVG line chart of trust score over time with
 * autonomy-threshold reference lines.
 *
 * Three horizontal dashed lines mark the autonomy thresholds the
 * scoring engine uses:
 *   ≥98  Self-autonomous (green)
 *   ≥90  Supervised      (amber)
 *   ≥75  Restricted      (red)
 * Below 75 = probation territory.
 *
 * Difference vs. the generic Sparkline: this chart knows about the
 * autonomy domain and draws domain-specific reference lines + a soft
 * fill, with grid + axis tick labels — designed for the Trust Timeline
 * waterfall above the timeline events.
 *
 * Phase A8 / G308.
 *
 * @param {Object} props
 * @param {Array<{ score:number, timestamp:string }>} props.points
 * @param {number} [props.width=540]
 * @param {number} [props.height=140]
 */
export function ScoreWaterfall({ points = [], width = 540, height = 140 }) {
  const clean = useMemo(() =>
    (points || [])
      .filter((p) => Number.isFinite(Number(p.score)))
      .map((p) => ({ score: Number(p.score), t: new Date(p.timestamp).getTime() }))
      .filter((p) => Number.isFinite(p.t))
      .sort((a, b) => a.t - b.t),
  [points]);

  if (clean.length < 2) {
    return (
      <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-4 text-[12px] text-v2-ink-muted">
        Not enough trust score history to render a waterfall — need at least 2 datapoints.
      </div>
    );
  }

  const PADDING = { top: 14, right: 16, bottom: 24, left: 36 };
  const W = width;
  const H = height;
  const innerW = W - PADDING.left - PADDING.right;
  const innerH = H - PADDING.top - PADDING.bottom;

  const tMin = clean[0].t;
  const tMax = clean[clean.length - 1].t;
  const tRange = Math.max(tMax - tMin, 1);

  /* Y-axis clamped to the data with a little headroom each side, but
   * never escapes 0-100. Keeping the visible range adaptive (rather
   * than always 0-100) makes small score moves readable. */
  const scoresOnly = clean.map((p) => p.score);
  const dataMin = Math.min(...scoresOnly);
  const dataMax = Math.max(...scoresOnly);
  const padScore = Math.max(5, (dataMax - dataMin) * 0.15);
  const yMin = Math.max(0, Math.floor(dataMin - padScore));
  const yMax = Math.min(100, Math.ceil(dataMax + padScore));
  const yRange = Math.max(yMax - yMin, 1);

  const xScale = (t) => PADDING.left + ((t - tMin) / tRange) * innerW;
  const yScale = (s) => PADDING.top + (1 - (s - yMin) / yRange) * innerH;

  const pathD = clean.map((p, i) =>
    `${i === 0 ? 'M' : 'L'} ${xScale(p.t).toFixed(2)} ${yScale(p.score).toFixed(2)}`,
  ).join(' ');

  const areaD = `${pathD} L ${xScale(clean[clean.length - 1].t).toFixed(2)} ${(PADDING.top + innerH).toFixed(2)} L ${xScale(clean[0].t).toFixed(2)} ${(PADDING.top + innerH).toFixed(2)} Z`;

  /* Autonomy threshold reference lines — only render if the value falls
   * inside the currently-visible y range, otherwise the legend would
   * lie about visible thresholds. */
  const thresholds = [
    { v: 98, label: '98 · Self-autonomous', stroke: 'var(--v2-success)' },
    { v: 90, label: '90 · Supervised',      stroke: 'var(--v2-warning)' },
    { v: 75, label: '75 · Restricted',      stroke: 'var(--v2-danger)' },
  ].filter((t) => t.v >= yMin && t.v <= yMax);

  const xTicks = useMemo(() => {
    const first = clean[0];
    const last = clean[clean.length - 1];
    const fmt = (t) => {
      const d = new Date(t);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    };
    return [
      { x: xScale(first.t), label: fmt(first.t) },
      { x: xScale(last.t), label: fmt(last.t) },
    ];
  }, [clean]);

  return (
    <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-3">
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" role="img"
           aria-label={`Trust score waterfall: ${clean.length} points from ${new Date(tMin).toLocaleDateString()} to ${new Date(tMax).toLocaleDateString()}`}>
        {/* Y-axis ticks (just min/max) */}
        <text x={PADDING.left - 6} y={PADDING.top + 4} textAnchor="end"
              fontSize={9.5} fill="var(--v2-ink-muted)">{yMax}</text>
        <text x={PADDING.left - 6} y={PADDING.top + innerH} textAnchor="end"
              fontSize={9.5} fill="var(--v2-ink-muted)">{yMin}</text>

        {/* Threshold reference lines */}
        {thresholds.map((t) => {
          const y = yScale(t.v);
          return (
            <g key={t.v}>
              <line x1={PADDING.left} x2={PADDING.left + innerW} y1={y} y2={y}
                    stroke={t.stroke} strokeOpacity={0.4} strokeWidth={1} strokeDasharray="3 3" />
              <text x={PADDING.left + innerW - 4} y={y - 3} textAnchor="end"
                    fontSize={9.5} fill={t.stroke} fillOpacity={0.85} fontWeight={600}>
                {t.v}
              </text>
            </g>
          );
        })}

        {/* Soft area fill under the line */}
        <path d={areaD} fill="var(--v2-action-soft)" fillOpacity={0.35} />

        {/* Score line */}
        <path d={pathD} fill="none" stroke="var(--v2-action)" strokeWidth={2}
              strokeLinecap="round" strokeLinejoin="round" />

        {/* Data points */}
        {clean.map((p, i) => (
          <circle key={i} cx={xScale(p.t)} cy={yScale(p.score)} r={2.5}
                  fill="var(--v2-bg-surface)" stroke="var(--v2-action)" strokeWidth={1.5} />
        ))}

        {/* X-axis tick labels */}
        {xTicks.map((tick, i) => (
          <text key={i} x={tick.x} y={H - 6} textAnchor={i === 0 ? 'start' : 'end'}
                fontSize={9.5} fill="var(--v2-ink-muted)">
            {tick.label}
          </text>
        ))}
      </svg>

      <div className="mt-2 flex items-center gap-4 text-[10.5px] text-v2-ink-muted">
        <LegendDot tone="success" /> 98 · Self-autonomous
        <LegendDot tone="warning" /> 90 · Supervised
        <LegendDot tone="danger" />  75 · Restricted
      </div>
    </div>
  );
}

function LegendDot({ tone }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block w-2 h-2 rounded-full ${
        tone === 'success' ? 'bg-v2-success' :
        tone === 'warning' ? 'bg-v2-warning' : 'bg-v2-danger'
      }`} />
    </span>
  );
}

export default ScoreWaterfall;
